import { skipToken, useInfiniteQuery } from '@tanstack/react-query';

import { Automations } from '@air/api';
import { type Succeeded } from '@air/server-sdk';
import { callAirApi } from '@air/utils-api';

export type Automation = Succeeded<typeof Automations.list>['data'][number];

export const PRIVATE_AUTOMATIONS_LIST_KEY = 'PRIVATE_AUTOMATIONS_LIST';

export type PrivateAutomationsListKey = [typeof PRIVATE_AUTOMATIONS_LIST_KEY, UsePrivateAutomationsListParams];

export const getPrivateAutomationsListKey = (params: UsePrivateAutomationsListParams): PrivateAutomationsListKey => [
  PRIVATE_AUTOMATIONS_LIST_KEY,
  params,
];

export type UsePrivateAutomationsListParams = Omit<
  NonNullable<Parameters<typeof Automations.list>[0]>,
  'workspaceId' | 'cursor'
> & {
  workspaceId: string | undefined;
};

export type UsePrivateAutomationsListOptions = {
  enabled?: boolean;
};

export const usePrivateAutomationsList = (
  params: UsePrivateAutomationsListParams,
  options?: UsePrivateAutomationsListOptions,
) => {
  const { workspaceId, limit } = params;

  const queryFn = !workspaceId
    ? skipToken
    : async ({ pageParam }: { pageParam: string }) =>
        callAirApi(() =>
          Automations.list({
            workspaceId,
            limit,
            cursor: pageParam || undefined,
          }),
        );

  return useInfiniteQuery({
    queryKey: getPrivateAutomationsListKey(params),
    queryFn,
    getNextPageParam: (lastPage) => (lastPage.pagination?.hasMore ? (lastPage.pagination.cursor ?? null) : null),
    initialPageParam: '',
    enabled: !!workspaceId && options?.enabled,
  });
};
